/* eslint-disable react/no-unescaped-entities */
import {
    Avatar,
    Button,
    Form,
    Icon,
    Input,
    Modal,
    notification,
    Upload,
    Switch,
    Tooltip,
} from "antd";
import { UploadChangeParam } from "antd/es/upload/interface";
import ImgCrop from "antd-img-crop";
import React from "react";

import { IUser, IUserProfile } from "../../../../Models/Models";
import { BuxTooltip } from "../../../Common/";
import { IOktaMFAFactor } from "../../../../Models/OktaModels/IOktaMFAFactor";
import MultiFactorModal from "./MultiFactorModal";
import { featureFlags } from "../../../../Common/CurrentFeatureFlags";
import TextArea from "antd/lib/input/TextArea";
import { IUserShareSettings } from "../../../../Models/IUserShareSettings";
import { topNavHeightPx } from "../../../../View/Products/Constants/StyleConstants";
import { hasFeature } from "../../../../Actions/UserActions";
import { ScoutFeature } from "../../../../Enums/ScoutFeature";

interface IUserProfileProps {
    user: IUser;
    userShareSettings: IUserShareSettings;
    allMFAFactors: IOktaMFAFactor[];
    selectedMFAFactorID: string;
    waitingForMFAResponse: boolean;
    mfaResult: string;
    showMfaEnrollmentModal: boolean;
    mfaEnrollmentModalText: string;
    mfaEnrollmentUpdatedThisSession: boolean;
    saveProfileLoading: boolean;
    updateUserProfile: (
        profileUpdateObj: IUserProfile,
        shareSettingsUpdateObj: IUserShareSettings
    ) => void;
    resetPassword: () => void;
    updateUserAvatar: (avatarImg: File) => void;
    deleteUserAvatar: (avatarStr: string) => void;
    updateShareImage: (shareImg: File) => void;
    deleteShareImage: (logoStr: string) => void;
    GetMFAFactors: (oktaUserID: string) => void;
    IssueMFAChallenge: (factor: IOktaMFAFactor) => void;
    IssuePushMFAChallenge: (factor: IOktaMFAFactor) => void;
    setAllMFAFactors: (factors: IOktaMFAFactor[]) => void;
    setSelectedFactorID: (id: string) => void;
    setWaitingForMFAResponse: (waiting: boolean) => void;
    SubmitMFAChallengeResponse: (
        factor: IOktaMFAFactor,
        response: string
    ) => void;
    PollForPushResponse: (factor: IOktaMFAFactor) => void;
    setShowMfaEnrollmentModal: (show: boolean) => void;
    setMfaEnrollmentUpdatedThisSession: (updated: boolean) => void;
}

interface IUserProfileState {
    userCopy: IUser;
    userShareSettingsCopy: IUserShareSettings;
    // profile stuff
    userProfileIsValid: boolean;
    usernameValid: boolean;
    firstNameValid: boolean;
    lastNameValid: boolean;
    emailValid: boolean;
    phoneValid: boolean;
    // avatar
    avatarFileList: any[];
    avatarRemoveAreYouSureModalVisible: boolean;
    shareLogoRemoveAreYouSureModalVisible: boolean;
    //MFA
    multifactorEnabled: boolean;
    showMFADisableWarning: boolean;
    showMFAEntryModal: boolean;
    showMFAEnrollmentModal: boolean;
    shareReportLogoList: any[];
}

class UserProfile extends React.Component<
    IUserProfileProps,
    IUserProfileState
> {
    private profileUpdateObj: IUserProfile = {
        userName: null,
        firstName: null,
        lastName: null,
        emailAddress: null,
        phone: null,
        title: null,
        currentPassword: null,
        newPassword: null,
        mfaEnrollUnenroll: null,
    };
    private shareSettingsUpdateObj: IUserShareSettings = {
        disclaimer: null,
    };
    private showUploadListObj: {
        showPreviewIcon: boolean;
        showRemoveIcon: boolean;
    } = {
        showPreviewIcon: false,
        showRemoveIcon: true,
    };
    /**
     *
     */
    constructor(props: IUserProfileProps) {
        super(props);

        // set defaults
        this.state = {
            userCopy: { ...this.props.user },
            userShareSettingsCopy: { ...this.props.userShareSettings },
            userProfileIsValid: false,
            usernameValid: true,
            firstNameValid: true,
            lastNameValid: true,
            emailValid: true,
            phoneValid: true,
            avatarFileList: [],
            avatarRemoveAreYouSureModalVisible: false,
            shareLogoRemoveAreYouSureModalVisible: false,
            multifactorEnabled: false,
            showMFADisableWarning: false,
            showMFAEntryModal: false,
            showMFAEnrollmentModal: false,
            shareReportLogoList: [],
        };
    }

    public componentDidMount(): void {
        this.setState({ multifactorEnabled: this.userHasMfa(this.props.user) });
    }

    public componentDidUpdate(prevProps: IUserProfileProps): void {
        //check if the user changed their first name, last name or email and update intercom if they did
        if (
            this.props.user.firstName !== prevProps.user.firstName
            || this.props.user.lastName !== prevProps.user.lastName
            || this.props.user.email !== prevProps.user.email
        ) {
            (window as any).Intercom("update", {
                name: this.props.user.firstName + " " + this.props.user.lastName,
                email: this.props.user.email,
            });
        }
        if (
            this.state.multifactorEnabled === false
            && this.props.mfaEnrollmentUpdatedThisSession
        ) {
            this.props.setMfaEnrollmentUpdatedThisSession(false);
        }
        //reset save button to disabled after successful update
        if (!this.props.saveProfileLoading && prevProps.saveProfileLoading) {
            this.resetProfileForm();
        }

        if (this.props.user.shareImage) {
            /** If the user has updated their share image */
            if (!prevProps.user.shareImage) {
                this.setState({
                    shareReportLogoList: [],
                });
            }
        } else {
            /** If the user has removed their share image. */
            if (prevProps.user.shareImage) {
                this.setState({
                    shareReportLogoList: [],
                });
            }
        }
    }

    public render(): JSX.Element {

        const hasImageUploadFeature = hasFeature(this.props.user, ScoutFeature.UserSettingsUploadImage);
        const hasMobilyticsPaidFeature = hasFeature(this.props.user, ScoutFeature.MobilyticsPaid);

        let avatarUploadButton: JSX.Element = null;
        if (this.state.avatarFileList.length === 0
            && !this.props.user.profileImage) {
            avatarUploadButton = (
                <>
                    <Icon type="plus" />
                    <div className="ant-upload-text">Upload</div>
                </>
            );
        } else if (this.props.user.profileImage
            && this.state.avatarFileList.length === 0) {
            avatarUploadButton = (
                <Avatar
                    src={`/Images/Users/Avatars/${this.props.user.profileImage}`}
                    size={96}
                />
            );
        }

        let shareLogoUploadButton: JSX.Element = null;
        if (this.state.shareReportLogoList.length === 0
            && !this.props.user.shareImage) {
            shareLogoUploadButton = (
                <>
                    <Icon type="plus" />
                    <div className="ant-upload-text">Upload</div>
                </>
            );
        } else if (this.props.user.shareImage
            && this.state.shareReportLogoList.length === 0) {
            shareLogoUploadButton = (
                <img
                    src={`/Images/Users/Share/${this.props.user.shareImage}`}
                    style={{
                        width: 234,
                        height: 57
                    }}
                />
            );
        }

        const avatarUploadArea: JSX.Element = featureFlags.DigitalShare.Footer
            ? (
                <ImgCrop
                    aspect={1 / 1}
                    quality={1}>
                    <Upload
                        listType="picture-card"
                        fileList={this.state.avatarFileList}
                        showUploadList={
                            this.state.avatarFileList.length === 1 && this.showUploadListObj
                        }
                        beforeUpload={this.beforeLogoChange}
                        onChange={this.handleAvatarChange}
                        disabled={this.props.saveProfileLoading}>
                        {avatarUploadButton}
                    </Upload>
                </ImgCrop>
            )
            : (
                <Upload
                    listType="picture-card"
                    fileList={this.state.avatarFileList}
                    showUploadList={
                        this.state.avatarFileList.length === 1 && this.showUploadListObj
                    }
                    beforeUpload={this.beforeLogoChange}
                    onChange={this.handleAvatarChange}
                    disabled={!hasImageUploadFeature || this.props.saveProfileLoading}
                    openFileDialogOnClick={hasImageUploadFeature}>
                    {avatarUploadButton}
                </Upload>
            );

        let shareLogoUploadArea: JSX.Element = null;
        shareLogoUploadArea = hasMobilyticsPaidFeature
            && featureFlags.DigitalShare.Footer
            ? (
                <ImgCrop
                    aspect={4 / 1}
                    quality={1}>
                    <Upload
                        listType="picture-card"
                        fileList={this.state.shareReportLogoList}
                        showUploadList={
                            this.state.shareReportLogoList.length === 1
                            && this.showUploadListObj
                        }
                        beforeUpload={this.beforeLogoChange}
                        onChange={this.onShareLogoChange}
                        disabled={this.props.saveProfileLoading}>
                        {shareLogoUploadButton}
                    </Upload>
                </ImgCrop>
            )
            : (
                <Upload
                    listType="picture-card"
                    fileList={this.state.shareReportLogoList}
                    showUploadList={
                        this.state.shareReportLogoList.length === 1
                        && this.showUploadListObj
                    }
                    beforeUpload={this.beforeLogoChange}
                    onChange={this.onShareLogoChange}
                    disabled={!hasImageUploadFeature || this.props.saveProfileLoading}
                    openFileDialogOnClick={hasImageUploadFeature}>
                    {shareLogoUploadButton}
                </Upload>
            );

        const userAvatarTrashCan: JSX.Element = this.props.user.profileImage
            && (
                <Icon
                    type="delete"
                    theme="outlined"
                    style={{ fontSize: "20px", cursor: "pointer" }}
                    onClick={() => this.toggleAreYouSureRemoveUserAvatar(true)}
                />
            );

        const shareLogoTrashCan: JSX.Element = hasMobilyticsPaidFeature
            && this.props.user.shareImage
            && (
                <Icon
                    type="delete"
                    theme="outlined"
                    style={{ fontSize: "20px", cursor: "pointer" }}
                    onClick={() => {
                        if (!this.props.saveProfileLoading) {
                            this.toggleAreYouSureRemoveShareLogo(true);
                        }
                    }}
                />
            );

        const mfaModal = this.props.allMFAFactors
            && this.props.allMFAFactors.length > 0
            && (
                <MultiFactorModal
                    allMFAFactors={this.props.allMFAFactors}
                    selectedMFAFactorID={this.props.selectedMFAFactorID}
                    waitingForMFAResponse={this.props.waitingForMFAResponse}
                    mfaResult={this.props.mfaResult}
                    setAllMFAFactors={this.props.setAllMFAFactors}
                    IssueMFAChallenge={this.props.IssueMFAChallenge}
                    IssuePushMFAChallenge={this.props.IssuePushMFAChallenge}
                    setSelectedFactorID={this.props.setSelectedFactorID}
                    setWaitingForMFAResponse={this.props.setWaitingForMFAResponse}
                    SubmitMFAChallengeResponse={this.props.SubmitMFAChallengeResponse}
                    PollForPushResponse={this.props.PollForPushResponse}
                    successCallback={this.onMFASuccess}
                />
            );

        const mfaEnrollmentModal = (
            <Modal
                visible={this.props.showMfaEnrollmentModal}
                maskClosable={false}
                closable={false}
                footer={[
                    <Button
                        key="logOutLater"
                        id="logOutLater"
                        onClick={this.onMfaEnrollmentModalOk}>
                        {this.props.mfaResult === "ERROR"
                            ? "Ok"
                            : "Log out later"}
                    </Button>,
                    <Button
                        key="logOutNow"
                        id="logOutNow"
                        type="primary"
                        onClick={this.logout}
                        hidden={this.props.mfaResult === "ERROR"}>
                        {/* Invisible form to help handle logout process */}
                        <form
                            id="mfaLogoutForm"
                            name="mfaLogoutForm"
                            action="/account/logout"
                            method="POST"
                            style={{ display: "hidden" }}
                        ></form>
                        Log out now (recommended)
                    </Button>,
                ]}>
                <h3>{this.props.mfaEnrollmentModalText}</h3>
            </Modal>
        );

        const mfaEnrollSwitch = (
            <Switch
                checkedChildren="ENABLED"
                unCheckedChildren="DISABLED"
                defaultChecked={this.userHasMfa(this.props.user)}
                checked={this.state.multifactorEnabled}
                onChange={this.mfaChangeHandler}
                loading={this.state.showMFADisableWarning}
                disabled={this.props.mfaEnrollmentUpdatedThisSession}
            />
        );

        let finalSwitch = this.props.mfaEnrollmentUpdatedThisSession
            ? (
                <Tooltip
                    placement="right"
                    title={
                        "You can't update this again this session. Please log out and back in if you need to enable or disable multi-factor authentication."
                    }
                >
                    {mfaEnrollSwitch}
                </Tooltip>
            )
            : mfaEnrollSwitch;

        if (this.props.user.company.enforceMFA) {
            finalSwitch = (
                <Tooltip
                    placement="right"
                    title={
                        "Your company has MFA enforced. Please contact your account administrator for questions."
                    }
                >
                    <i>This feature is disabled</i>
                </Tooltip>
            );
        }

        return (
            <div className="UserProfile">
                {(
                    <Modal
                        title="Disable multi-factor authentication?"
                        visible={this.state.showMFADisableWarning}
                        onOk={this.onMSADisableConfirm}
                        onCancel={this.onMSADisableCancel}
                        maskClosable={false}
                        closable={false}
                    >
                        <h3>
                            Are you sure you want to disable multi-factor authentication (MFA)?
                            Your account will be better protected against unauthorized access with
                            it enabled.
                        </h3>
                        <br />
                        <p>
                            Note: this change will not take effect until you click "Save" below,
                            and verify your identity one last time. If you have lost or replaced
                            the device on which you registered your MFA, please contact SCOUT
                            Support to reset or remove your MFA.
                        </p>
                    </Modal>
                )}
                {(
                    <Modal
                        title="Remove User Avatar?"
                        visible={this.state.avatarRemoveAreYouSureModalVisible}
                        onCancel={() => this.toggleAreYouSureRemoveUserAvatar(false)}
                        onOk={this.removeUserAvatar}>
                        <p>Are you sure you want to remove your user avatar?</p>
                    </Modal>
                )}
                {(
                    <Modal
                        title="Remove Share Logo?"
                        visible={this.state.shareLogoRemoveAreYouSureModalVisible}
                        onCancel={() => this.toggleAreYouSureRemoveShareLogo(false)}
                        onOk={this.removeShareLogo}>
                        <p>Are you sure you want to remove your share logo?</p>
                    </Modal>
                )}
                {mfaModal}
                {mfaEnrollmentModal}
                <h2 className="settings-title">User Profile</h2>
                <div className="flex-container">
                    <div className="image-upload-sidebar">

                        {hasMobilyticsPaidFeature && (
                            <div className="image-upload-container image-upload-company-logo">
                                <h3>COMPANY LOGO:</h3>
                                <p>Upload your company for use in "Print" and "Share" exports.</p>
                                <div style={{
                                    display: "flex",
                                    justifyContent: "center"
                                }}>
                                    <div style={{
                                        width: this.state.shareReportLogoList.length === 0
                                            ? "260px"
                                            : "104px" // antd doesn't give us a way to 
                                        // control the size of the 
                                        // picture cards. We are able to
                                        // override it if nothing has
                                        // been uploaded, or if the
                                        // upload is complete, but 
                                        // I didn't see a good way to do
                                        // it while the picture is
                                        // uploading. So instead, we're
                                        // using this CSS rule to center
                                        // it within the container.
                                    }}>
                                        {shareLogoUploadArea}
                                    </div>
                                </div>
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "260px",
                                    columnGap: "16px"
                                }}>
                                    <Button
                                        type="default"
                                        style={{
                                            width: 104,
                                            marginRight: shareLogoTrashCan
                                                ? ".5rem"
                                                : "initial"
                                        }}
                                        disabled={this.state.shareReportLogoList.length === 0
                                            || this.props.saveProfileLoading}
                                        loading={this.props.saveProfileLoading}
                                        onClick={this.initiateShareImageUpdate}>Update</Button>
                                    {shareLogoTrashCan}
                                </div>
                            </div>
                        )}
                        <div className="image-upload-container">
                            <h3>PROFILE PHOTO:</h3>
                            <p>Upload your profile photo for use in "Print" and "Share" exports.</p>
                            <div style={{
                                display: "flex",
                                justifyContent: "center"
                            }}>
                                <div style={{
                                    width: "104px"
                                }}>
                                    {avatarUploadArea}
                                </div>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    marginTop: "9px",
                                    width: "260px",
                                    columnGap: "16px"
                                }}>
                                <Button
                                    type="default"
                                    style={{
                                        width: 104,
                                        marginRight: userAvatarTrashCan
                                            ? ".5rem"
                                            : "initial"
                                    }}
                                    disabled={this.state.avatarFileList.length === 0}
                                    onClick={this.beginUserAvatarUpdate}>
                                    Update
                                </Button>
                                {userAvatarTrashCan}
                            </div>
                        </div>
                    </div>
                    <div className="form-containar">
                        <Form
                            layout="horizontal"
                            style={{ marginBottom: "50px" }}
                            name="userProfileForm">
                            <Form.Item
                                label={
                                    <>
                                        <span>EMAIL</span>
                                        <BuxTooltip
                                            placement="right"
                                            content="Email must be unique and cannot be an @buxtonco.com email."
                                            trigger="click"
                                            allowClickThrough={false}
                                            style={{
                                                position: "absolute",
                                                right: 2,
                                                top: 0,
                                                marginTop: "12px",
                                            }}
                                        />
                                    </>
                                }
                            >
                                <Input
                                    placeholder="User Name"
                                    name="userName"
                                    className={!this.state.usernameValid ? "invalid" : null}
                                    defaultValue={this.state.userCopy.userName}
                                    onChange={this.validateUserProfileForm}
                                />
                            </Form.Item>
                            <Form.Item label="FIRST NAME">
                                <Input
                                    placeholder="First Name"
                                    name="firstName"
                                    className={!this.state.firstNameValid ? "invalid" : null}
                                    defaultValue={this.state.userCopy.firstName}
                                    onChange={this.validateUserProfileForm}
                                />
                            </Form.Item>
                            <Form.Item label="LAST NAME">
                                <Input
                                    placeholder="Last Name"
                                    name="lastName"
                                    className={!this.state.lastNameValid ? "invalid" : null}
                                    defaultValue={this.state.userCopy.lastName}
                                    onChange={this.validateUserProfileForm}
                                />
                            </Form.Item>
                            <Form.Item label="TITLE">
                                <Input
                                    placeholder="Title"
                                    name="title"
                                    defaultValue={this.state.userCopy.title}
                                    onChange={this.validateUserProfileForm}
                                />
                            </Form.Item>
                            <Form.Item
                                label={
                                    <>
                                        <span>PHONE NUMBER</span>
                                        <BuxTooltip
                                            placement="right"
                                            content="This phone number will be displayed in the contact info section when you share or print Retail Match and Mobilytics reports."
                                            trigger="click"
                                            allowClickThrough={false}
                                            style={{
                                                position: "absolute",
                                                right: 2,
                                                top: 0,
                                                marginTop: "12px",
                                            }}
                                        />
                                    </>
                                }
                            >
                                <Input
                                    placeholder="817-555-1234"
                                    name="phone"
                                    maxLength={24}
                                    className={!this.state.phoneValid ? "invalid" : null}
                                    defaultValue={this.state.userCopy.phone}
                                    onChange={this.validateUserProfileForm}
                                />
                            </Form.Item>
                            <Form.Item
                                label={
                                    <>
                                        <span>DISCLAIMER TEXT</span>
                                        <BuxTooltip
                                            placement="right"
                                            content="Use this section to add disclaimers or additional explanation language to the footer bar of your exported Match and Mobilytics reports."
                                            trigger="click"
                                            allowClickThrough={false}
                                            style={{
                                                position: "absolute",
                                                right: 2,
                                                top: 0,
                                                marginTop: "12px",
                                            }}
                                        />
                                    </>
                                }
                            >
                                <TextArea
                                    placeholder="Disclaimer Text"
                                    name="shareDisclaimer"
                                    maxLength={400}
                                    rows={5}
                                    defaultValue={this.state.userShareSettingsCopy.disclaimer}
                                    onChange={this.validateUserProfileForm}
                                />
                            </Form.Item>
                            <Form.Item
                                label={
                                    <>
                                        <span>MULTI-FACTOR AUTHENTICATION</span>
                                        <BuxTooltip
                                            placement="right"
                                            content="Multi-factor authentication provides an extra layer of security to your login experience. If enabled, in addition to your username and password, you will be required to enter a code that is delivered to you either by text message or a smartphone app. This way, even if someone guesses your password, they would have to also have access to your mobile device to access your account."
                                            trigger="click"
                                            allowClickThrough={false}
                                            style={{
                                                position: "absolute",
                                                right: 2,
                                                top: 0,
                                                marginTop: "12px",
                                            }}
                                        />
                                    </>
                                }
                            >
                                {finalSwitch}
                            </Form.Item>
                            <Button
                                loading={this.props.saveProfileLoading}
                                type={this.state.userProfileIsValid ? "primary" : null}
                                className="right"
                                disabled={!this.state.userProfileIsValid}
                                onClick={this.saveUserUpdate}
                            >Save
                            </Button>
                            <div className="clear"></div>
                        </Form>

                        <Form
                            layout="horizontal"
                            name="resetPasswordForm"
                            style={{ marginBottom: topNavHeightPx }}>
                            <Button
                                type="primary"
                                className="left"
                                onClick={this.resetPassword}
                            >Reset Password
                            </Button>
                            <div className="clear"></div>
                        </Form>
                    </div>
                </div>
            </div>
        );
    }

    private validateUserProfileForm = (): void => {
        const form: HTMLFormElement = document.forms["userProfileForm"];
        const uName: string = form["userName"].value;
        const fName: string = form["firstName"].value;
        const lName: string = form["lastName"].value;
        /** GB - not sure why typescript is yelling at me for this one input's type
         * compared to the others. They are all the same.
         */
        const title: string = (form["title"] as any).value;
        const phone: string = form["phone"].value;
        const shareDisclaimer: string = form["shareDisclaimer"].value;

        const isValid
            = this.checkIfEmailAndNotBuxtonco(uName)
            && this.firstNameIsValid(fName)
            && this.lastNameIsValid(lName)
            && this.phoneIsValid(phone);

        if (isValid) {
            this.profileUpdateObj.userName = uName;
            this.profileUpdateObj.emailAddress = uName;
            this.profileUpdateObj.firstName = fName;
            this.profileUpdateObj.lastName = lName;
            this.profileUpdateObj.phone = phone;
            this.profileUpdateObj.title = title;
            this.shareSettingsUpdateObj.disclaimer = shareDisclaimer;

            //This is not necessarily the value of the switch. It is an indicator to enroll, unenroll, or do nothing
            //If the switch value is different from the user object, use the switch value
            this.profileUpdateObj.mfaEnrollUnenroll
                = this.state.multifactorEnabled !== this.userHasMfa(this.props.user)
                    ? this.state.multifactorEnabled
                    : null;
        }

        this.setState({ userProfileIsValid: isValid });
    };

    private firstNameIsValid = (fName: string): boolean => {
        this.setState({
            firstNameValid: fName && fName.length > 0,
        });

        return fName && fName.length > 0;
    };

    private lastNameIsValid = (lName: string): boolean => {
        this.setState({
            lastNameValid: lName && lName.length > 0,
        });

        return lName && lName.length > 0;
    };

    private checkIfEmailAndNotBuxtonco = (email: string): boolean => {
        const re
            = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        const emailTest = re.test(email) && email.indexOf("@buxtonco.com") === -1;

        this.setState({
            emailValid: emailTest,
        });

        return emailTest;
    };

    private phoneIsValid = (phone: string): boolean => {
        if (phone) {
            /**
             * We originally had a regular expression here to test for numbers/hyphens,
             * but we're letting the user include anything to account for extensions,
             * text numbers, custom formatting, etc.
             */
            const phoneTest = phone.length <= 24;
            this.setState({ phoneValid: phoneTest });
            return phoneTest;
        }
        this.setState({ phoneValid: true });
        // Phone is optional
        return true;
    };

    private resetPassword = (): void => {
        this.props.resetPassword();
    };

    private beforeLogoChange = (info: File): boolean => {
        const isValidExtension: boolean = this.logoIsValidFileExtension(info);
        if (!isValidExtension) {
            notification.open({
                message: "Invalid Format",
                description: "Image must be either .png, .jpeg, .jpg, or .svg",
            });
        }

        return isValidExtension;
    };

    private logoIsValidFileExtension = (info: File): boolean => {
        const fileType = info.type.toLowerCase();
        return fileType.includes("jpeg")
            || fileType.includes("jpg")
            || fileType.includes("png")
            || fileType.includes("svg");
    };

    private handleAvatarChange = (info: UploadChangeParam): void => {
        if (info.fileList.length > 0
            && this.logoIsValidFileExtension(info.fileList[0].originFileObj as any)) {
            this.setState({
                avatarFileList: [info.fileList[0]],
            });
        } else {
            this.setState({
                avatarFileList: [],
            });
        }
    };

    private onShareLogoChange = (info: UploadChangeParam): void => {
        if (info.fileList.length > 0
            && this.logoIsValidFileExtension(info.fileList[0].originFileObj as any)) {
            this.setState({
                shareReportLogoList: [info.fileList[0]],
            });
        } else {
            this.setState({
                shareReportLogoList: [],
            });
        }
    };

    private beginUserAvatarUpdate = (): void => {
        if (this.state.avatarFileList.length === 0) {
            notification.open({
                message: "No Image.",
                description: "Please choose an image before uploading",
            });
        } else {
            const avatarImg: File = this.state.avatarFileList[0].originFileObj;
            this.props.updateUserAvatar(avatarImg);
            this.setState({
                avatarFileList: []
            });
        }
    };

    private initiateShareImageUpdate = (): void => {
        if (this.state.shareReportLogoList.length === 0) {
            notification.open({
                message: "No Image.",
                description: "Please choose an image before uploading",
            });
        } else {
            const img: File = this.state.shareReportLogoList[0].originFileObj;
            this.props.updateShareImage(img);
            this.setState({
                shareReportLogoList: []
            });
        }
    };

    private saveUserUpdate = (): void => {
        if (this.userHasMfa(this.props.user)) {
            this.props.GetMFAFactors(this.props.user.oktaUserID);
        } else {
            this.props.updateUserProfile(
                this.profileUpdateObj,
                this.shareSettingsUpdateObj
            );
        }
    };

    private runBuxtonChecksOnProfile = (): boolean => {
        if (this.state.userCopy.userName.toLowerCase().endsWith("-setup")) {
            notification.open({
                message: "Invalid Username",
                description: "Username cannot end with -setup",
            });
            return false;
        } else if (this.state.userCopy.userName.toLowerCase().endsWith("-demo")) {
            notification.open({
                message: "Invalid Username",
                description: "Username cannot end with -demo",
            });
            return false;
        }

        return true; // everything is ok
    };

    private toggleAreYouSureRemoveUserAvatar = (show: boolean): void => {
        this.setState({
            avatarRemoveAreYouSureModalVisible: show,
        });
    };

    private toggleAreYouSureRemoveShareLogo = (show: boolean): void => {
        this.setState({
            shareLogoRemoveAreYouSureModalVisible: show,
        });
    };

    private removeUserAvatar = (): void => {
        this.props.deleteUserAvatar(this.props.user.profileImage);
        this.toggleAreYouSureRemoveUserAvatar(false);
    };

    private removeShareLogo = (): void => {
        this.props.deleteShareImage(this.props.user.shareImage);
        this.toggleAreYouSureRemoveShareLogo(false);
    };

    private resetProfileForm = (): void => {
        // manually update state
        this.setState({
            userProfileIsValid: false,
        });
    };

    private mfaChangeHandler = (checked: boolean) => {
        if (checked) {
            this.setState({ multifactorEnabled: true }, this.validateUserProfileForm);
            //Need to ensure validate happens after state change occurs
        } else {
            this.setState({ showMFADisableWarning: true });
        }
    };

    private onMSADisableConfirm = (): void => {
        this.setState(
            { multifactorEnabled: false, showMFADisableWarning: false },
            this.validateUserProfileForm
        );
    };

    private onMSADisableCancel = (): void => {
        this.setState({ showMFADisableWarning: false });
    };

    private onMFASuccess = (): void => {
        this.props.updateUserProfile(
            this.profileUpdateObj,
            this.shareSettingsUpdateObj
        );
    };

    private onMfaEnrollmentModalOk = (): void => {
        this.props.setShowMfaEnrollmentModal(false);
        this.props.setMfaEnrollmentUpdatedThisSession(true);
    };

    private userHasMfa = (user: IUser): boolean => {
        const hasMfa = user
            && user.oktaGroups
            && user.oktaGroups.find((group) => {
                return group === "Multi-factor authentication opt-in";
            })
            ? true
            : false;

        return hasMfa;
    };

    private logout = (): void => {
        const form = document.getElementById("mfaLogoutForm") as HTMLFormElement;
        form.submit();
    };
}

export default UserProfile;
